// ───────────────────────────────────────────────────────────────────────────
// Demo core: constants
// CANONICAL COPY lives in docs/demo-core/. Each app gets a byte-identical copy in
// <app>/src/demo/core/ via scripts/sync-demo-core.sh. Edit the canonical copy
// only, then re-run the sync script (scripts/check-demo-core-sync.sh guards drift).
//
// This module (and the whole core) must not import anything from any app.
// ───────────────────────────────────────────────────────────────────────────

/** sessionStorage key holding 'true' while the tab is in demo mode. */
export const DEMO_FLAG_KEY = 'recycleeDemoMode';
/** sessionStorage key holding the persona the demo was opened with. */
export const DEMO_PERSONA_KEY = 'recycleeDemoPersona';

export const DEMO_OUTLET_ORG_ID = '00000000-0000-4000-8000-00000000a001';
export const DEMO_RECYCLER_ORG_ID = '00000000-0000-4000-8000-00000000a002';

export const DEMO_USER_ID = '00000000-0000-4000-8000-00000000f001';
/** Deliberately not one of the platform-admin usernames. */
export const DEMO_USERNAME = 'demo@recyclee.io';

export const DEMO_OUTLET_NAME = 'Al Nakheel Markets Outlet';
export const DEMO_OUTLET_NAME_AR = 'منافذ النخيل';
export const DEMO_RECYCLER_NAME = 'Riyadh Green Recycling Co.';
export const DEMO_RECYCLER_NAME_AR = 'شركة الرياض الخضراء لإعادة التدوير';

export interface LatLng {
  lat: number;
  lng: number;
}

export const RIYADH: LatLng = { lat: 24.7136, lng: 46.6753 };
export const JEDDAH: LatLng = { lat: 21.4858, lng: 39.1925 };

// ── Ids ────────────────────────────────────────────────────────────────────
// Every demo id is UUID-shaped and valid hex:
//   00000000-0000-4000-8000-0000 + <2-hex kind code> + <6-hex n>
// e.g. ids.site(1) === '00000000-0000-4000-8000-0000a1000001'.

/** Kind name -> 2-hex-digit kind code. Codes must stay unique. */
export const DEMO_ID_KINDS = {
  site: 'a1',
  pickupOrder: 'a2',
  preOrder: 'a3',
  delivery: 'a4',
  appetiteLine: 'a5',
  material: 'ab',
  category: 'ac',
  changeRequest: 'ad',
  truck: 'b1',
  supplier: 'b2',
  agreementPrice: 'b3',
  container: 'c1',
  zone: 'c2',
  collectionPoint: 'c3',
  notification: 'cc',
  driver: 'd1',
  staff: 'd2',
  manager: 'd3',
  user: 'd4',
  approval: 'dd',
  document: 'df',
  sensor: 'e1',
  route: 'e2',
  routeRun: 'e3',
  schedule: 'e4',
  training: 'ee',
  hseRecord: 'ef',
  facility: 'f1',
  upload: 'f2',
  utilityRecord: 'f3',
} as const;

export type DemoIdKind = keyof typeof DEMO_ID_KINDS;

const HEX2 = /^[0-9a-f]{2}$/i;
const MAX_N = 0xffffff;

/**
 * Build a demo id. `prefix` is either a kind name from DEMO_ID_KINDS ('site')
 * or a raw 2-hex-digit kind code ('a1'). `n` is an integer 0..16777215.
 * Throws on an invalid prefix or n, so a typo never produces a colliding id.
 */
export function demoId(prefix: string, n: number): string {
  const code = Object.prototype.hasOwnProperty.call(DEMO_ID_KINDS, prefix)
    ? DEMO_ID_KINDS[prefix as DemoIdKind]
    : prefix;
  if (!HEX2.test(code)) {
    throw new Error(`[demo] demoId: invalid kind "${prefix}" (use a DEMO_ID_KINDS name or 2 hex digits)`);
  }
  if (!Number.isInteger(n) || n < 0 || n > MAX_N) {
    throw new Error(`[demo] demoId: n must be an integer 0..${MAX_N}, got ${n}`);
  }
  return `00000000-0000-4000-8000-0000${code.toLowerCase()}${n.toString(16).padStart(6, '0')}`;
}

function kindFactory(kind: DemoIdKind): (n: number) => string {
  return (n: number) => demoId(kind, n);
}

/** `ids.site(1)`, `ids.container(3)`, … one factory per DEMO_ID_KINDS entry. */
export const ids: { readonly [K in DemoIdKind]: (n: number) => string } = {
  site: kindFactory('site'),
  pickupOrder: kindFactory('pickupOrder'),
  preOrder: kindFactory('preOrder'),
  delivery: kindFactory('delivery'),
  appetiteLine: kindFactory('appetiteLine'),
  material: kindFactory('material'),
  category: kindFactory('category'),
  changeRequest: kindFactory('changeRequest'),
  truck: kindFactory('truck'),
  supplier: kindFactory('supplier'),
  agreementPrice: kindFactory('agreementPrice'),
  container: kindFactory('container'),
  zone: kindFactory('zone'),
  collectionPoint: kindFactory('collectionPoint'),
  notification: kindFactory('notification'),
  driver: kindFactory('driver'),
  staff: kindFactory('staff'),
  manager: kindFactory('manager'),
  user: kindFactory('user'),
  approval: kindFactory('approval'),
  document: kindFactory('document'),
  sensor: kindFactory('sensor'),
  route: kindFactory('route'),
  routeRun: kindFactory('routeRun'),
  schedule: kindFactory('schedule'),
  training: kindFactory('training'),
  hseRecord: kindFactory('hseRecord'),
  facility: kindFactory('facility'),
  upload: kindFactory('upload'),
  utilityRecord: kindFactory('utilityRecord'),
};

/** Every id produced by demoId/ids matches this. */
export const DEMO_ID_PATTERN = /^00000000-0000-4000-8000-0000[0-9a-f]{8}$/;
